import React from "react";
import { ClipboardList, TrendingDown } from "lucide-react";
import { CaixaRegistradoraIcon, MesaIcon, MotoEntregaIcon, TotemIcon } from "@/components/icons/ModulosIcons";

import { formatarQtd } from "../../../lib/unidadeVenda";
import { formatCurrency } from "../lib/formato";

const NucleoLogo = ({ className }) => <img src="/logo-nucleo.png" alt="Nucleo" className={className} style={{ objectFit: "contain" }} />;

// As cinco colunas que o backend do Relatório de Produtos devolve. iFood, Mesa
// QR e robô do WhatsApp entram em "Cardápio" aqui (limitação conhecida, ver ficha).
export const CANAIS_PRODUTO = ["Mesas", "Comandas", "Balcão", "Totem", "Delivery", "Cardápio"];
const CFG = {
  Mesas: { barra: "bg-blue-500", texto: "text-blue-600 dark:text-blue-400", fundo: "from-blue-500/15 to-blue-500/5 ring-1 ring-blue-500/30", icone: "bg-blue-500/20", Icon: MesaIcon },
  Comandas: { barra: "bg-amber-500", texto: "text-amber-600 dark:text-amber-400", fundo: "from-amber-500/15 to-amber-500/5 ring-1 ring-amber-500/30", icone: "bg-amber-500/20", Icon: ClipboardList },
  "Balcão": { barra: "bg-purple-500", texto: "text-purple-600 dark:text-purple-400", fundo: "from-purple-500/15 to-purple-500/5 ring-1 ring-purple-500/30", icone: "bg-purple-500/20", Icon: CaixaRegistradoraIcon },
  Totem: { barra: "bg-violet-500", texto: "text-violet-600 dark:text-violet-400", fundo: "from-pink-500/15 to-pink-500/5 ring-1 ring-pink-500/30", icone: "bg-pink-500/20", Icon: TotemIcon },
  Delivery: { barra: "bg-orange-500", texto: "text-orange-600 dark:text-orange-400", fundo: "from-orange-500/15 to-orange-500/5 ring-1 ring-orange-500/30", icone: "bg-orange-500/20", Icon: MotoEntregaIcon },
  "Cardápio": { barra: "bg-pink-500", texto: "text-pink-600 dark:text-pink-400", fundo: "from-pink-500/15 to-pink-500/5 ring-1 ring-pink-500/30", icone: "bg-pink-500/20", Icon: NucleoLogo },
};

function CardCanal({ nome, dado, totalQtd, unidade }) {
  const cfg = CFG[nome];
  const pct = totalQtd > 0 ? (dado.qtd / totalQtd) * 100 : 0;
  const vazio = !dado.qtd;
  return (
    <div className={`relative rounded-xl border-2 overflow-hidden ${vazio ? "border-border/50 bg-muted/20 opacity-50" : `border-transparent bg-gradient-to-br ${cfg.fundo}`}`}>
      <div className="p-4">
        <div className="flex items-center justify-between mb-2.5">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${cfg.icone}`}><cfg.Icon className={`w-4.5 h-4.5 ${cfg.texto}`} /></div>
          <span className={`text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded-md ${vazio ? "text-muted-foreground bg-muted/40" : `${cfg.texto} bg-card/60`}`}>{pct.toFixed(1).replace(".", ",")}%</span>
        </div>
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">{nome}</p>
        <div className="flex items-baseline gap-1.5">
          <p className={`text-3xl font-black tabular-nums leading-none ${vazio ? "text-muted-foreground/60" : "text-foreground"}`}>{formatarQtd(dado.qtd, unidade).replace(/ (kg|L)$/, "")}</p>
          <span className="text-xs text-muted-foreground">{unidade || "un"}</span>
        </div>
        <p className={`text-sm font-mono font-semibold tabular-nums mt-1.5 ${vazio ? "text-muted-foreground/60" : "text-emerald-600 dark:text-emerald-400"}`}>{formatCurrency(dado.valor)}</p>
      </div>
      <div className="h-1 bg-muted/40"><div className={`h-full ${cfg.barra} transition-all`} style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

function CardCusto({ p, cmv }) {
  return (
    <div className="relative rounded-xl border-2 border-transparent bg-gradient-to-br from-red-500/15 to-red-500/5 ring-1 ring-red-500/30 overflow-hidden">
      <div className="p-4">
        <div className="flex items-center justify-between mb-2.5">
          <div className="w-9 h-9 rounded-lg bg-red-500/20 flex items-center justify-center"><TrendingDown className="w-4.5 h-4.5 text-red-600 dark:text-red-400" /></div>
        </div>
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">{p.unidade ? `Custo por ${p.unidade}` : "Custo unitário"}</p>
        <p className={`text-2xl font-black tabular-nums leading-none ${p.cmv_unit > 0 ? "text-red-600 dark:text-red-400" : "text-muted-foreground/60"}`}>
          {p.cmv_unit > 0 ? formatCurrency(p.cmv_unit) : "—"}
        </p>
        <p className="text-[10px] text-muted-foreground mt-2">
          {p.cmv_unit > 0 ? <>Total: <span className="font-mono font-semibold text-red-500">{formatCurrency(cmv)}</span></> : "Sem ficha técnica"}
        </p>
      </div>
      <div className="h-1 bg-red-500/30" />
    </div>
  );
}

// Linha aberta de um produto: venda por canal + custo unitário.
export default function DetalheCanais({ p, cmv }) {
  const modulos = p.por_modulo || {};
  const totalQtd = CANAIS_PRODUTO.reduce((s, m) => s + (modulos[m]?.qtd || 0), 0);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-6 gap-3">
      {CANAIS_PRODUTO.map((m) => <CardCanal key={m} nome={m} dado={modulos[m] || { qtd: 0, valor: 0 }} totalQtd={totalQtd} unidade={p.unidade} />)}
      <CardCusto p={p} cmv={cmv} />
    </div>
  );
}
